import { createContext, useContext, useEffect, useState, ReactNode, useMemo, useRef } from 'react';
import { MagicLinkAuthService } from '../services/MagicLinkAuthService';
import type { User, AuthContextType, AuthConfig } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  config: AuthConfig;
  onAuthChange?: (user: User | null) => void;
}

export function AuthProvider({ children, config, onAuthChange }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);

  // Create service instance
  const authService = useMemo(() => new MagicLinkAuthService({
    apiBaseUrl: config.apiBaseUrl,
  }), [config.apiBaseUrl]);

  /**
   * Hydrate state from localStorage on mount
   * This is the ONLY place we read from storage
   */
  const hydrateFromStorage = () => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('auth_token');
      const storedExpiresAt = localStorage.getItem('token_expires_at');

      if (storedUser && storedToken) {
        const parsedUser = JSON.parse(storedUser) as User;
        setUser(parsedUser);
        setToken(storedToken);
        
        if (storedExpiresAt) {
          setTokenExpiresAt(parseInt(storedExpiresAt, 10));
        }
        
        onAuthChange?.(parsedUser);
      }
    } catch (error) {
      console.error('[AuthContext] Failed to hydrate from storage:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Persist state to localStorage
   * This is the ONLY place we write to storage
   */
  const persistToStorage = (userData: User | null, tokenData: string | null) => {
    try {
      if (userData && tokenData) {
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('auth_token', tokenData);
        localStorage.setItem('last_activity', new Date().toISOString());
      } else {
        localStorage.removeItem('user');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('last_activity');
      }
    } catch (error) {
      console.error('[AuthContext] Failed to persist to storage:', error);
    }
  };

  /**
   * Fetch accounts for user
   */
  const fetchAccountsForUser = async (authToken: string) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/accounts`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.accounts || [];
      }

      return null;
    } catch (error) {
      console.error('[AuthContext] Error fetching accounts:', error);
      return null;
    }
  };

  const requestPin = async (email: string) => {
    return await authService.requestPin(email);
  };

  /**
   * Login with PIN code
   * After successful login, fetch accounts and select first one
   */
  const login = async (email: string, pinCode: string) => {
    try {
      setIsLoading(true);
      
      // Verify PIN and get tokens
      const authResponse = await authService.verifyPin(email, pinCode);
      let currentUser = authResponse.user as User;
      const currentToken = authResponse.token;
      const refreshToken = authResponse.refreshToken;
      const expiresIn = authResponse.expiresIn || 3600; // Default to 1 hour
      
      // Store refresh token (only place we store it)
      localStorage.setItem('refresh_token', refreshToken);
      
      // Calculate expiry time
      const expiresAt = Date.now() + (expiresIn * 1000);
      setToken(currentToken);
      setTokenExpiresAt(expiresAt);
      localStorage.setItem('token_expires_at', expiresAt.toString());

      // Fetch accounts
      const accounts = await fetchAccountsForUser(currentToken);

      // If we have accounts, select the first one
      if (accounts && accounts.length > 0) {
        const firstAccount = accounts[0];
        const accountId = firstAccount.accountId || firstAccount.id;

        if (accountId) {
          currentUser = {
            ...currentUser,
            accountId,
            companyName: firstAccount.companyName || firstAccount.company_name,
            role: firstAccount.role || currentUser.role,
          };
        }
      }

      // Update state and persist
      setUser(currentUser);
      setToken(currentToken);
      persistToStorage(currentUser, currentToken);
      onAuthChange?.(currentUser);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  /**
   * Load all accounts for current user
   * Uses current token from state
   */
  const loadAccounts = async () => {
    if (!token) {
      console.error('[AuthContext] No token available');
      return [];
    }
    return await fetchAccountsForUser(token);
  };

  /**
   * Switch to a different account
   */
  const switchAccount = async (accountId: string) => {
    if (!user) {
      console.error('[AuthContext] No user available');
      return;
    }

    try {
      setIsLoading(true);
      
      // Fetch all accounts to get the selected one
      const accounts = await fetchAccountsForUser(token);
      
      if (!accounts) {
        console.error('[AuthContext] Failed to fetch accounts');
        return;
      }

      // Find the selected account
      const selectedAccount = accounts.find(
        (acc: any) => (acc.accountId || acc.id) === accountId
      );

      if (!selectedAccount) {
        console.error('[AuthContext] Account not found:', accountId);
        return;
      }

      // Update user with new account - no token refresh needed!
      // The X-Account-Id header will be sent automatically by authorizedFetch
      const updatedUser = {
        ...user,
        accountId: selectedAccount.accountId || selectedAccount.id,
        companyName: selectedAccount.companyName || selectedAccount.company_name,
        role: selectedAccount.role || user.role,
      };

      setUser(updatedUser);
      persistToStorage(updatedUser, token);
      onAuthChange?.(updatedUser);
    } catch (error) {
      console.error('[AuthContext] Switch account error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      
      // Call logout API if needed
      const currentToken = token;
      if (currentToken) {
        try {
          await fetch(`${config.apiBaseUrl}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${currentToken}`,
            },
          });
        } catch (error) {
          console.error('[AuthContext] Logout API call failed:', error);
        }
      }
      
      // Clear state and storage
      setUser(null);
      setToken(null);
      persistToStorage(null, null);
      onAuthChange?.(null);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Proactively refresh token before it expires
   */
  const refreshTokenProactively = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return;

    try {
      const refreshResponse = await authService.refreshAccessToken(refreshToken);
      
      if (refreshResponse) {
        const newToken = refreshResponse.token;
        const newRefreshToken = refreshResponse.refreshToken;
        const expiresIn = refreshResponse.expiresIn || 3600;
        
        // Calculate new expiry time
        const expiresAt = Date.now() + (expiresIn * 1000);
        
        setToken(newToken);
        setTokenExpiresAt(expiresAt);
        localStorage.setItem('auth_token', newToken);
        localStorage.setItem('refresh_token', newRefreshToken);
        localStorage.setItem('token_expires_at', expiresAt.toString());
      }
    } catch (error) {
      console.error('[AuthContext] Proactive token refresh failed:', error);
    }
  };

  // Hydrate state from storage on mount (only runs once)
  useEffect(() => {
    hydrateFromStorage();
  }, []);

  // Persist state to storage whenever user or token changes
  // Use ref to prevent unnecessary writes when objects change reference but values are the same
  const lastPersistedRef = useRef<{ userId?: string; token?: string }>({});
  
  useEffect(() => {
    if (user && token) {
      if (lastPersistedRef.current.userId !== user.id || lastPersistedRef.current.token !== token) {
        persistToStorage(user, token);
        lastPersistedRef.current = { userId: user.id, token };
      }
    }
  }, [user, token]);

  // Set up proactive token refresh - refresh 5 minutes before expiry
  useEffect(() => {
    if (!token || !tokenExpiresAt) return;

    const now = Date.now();
    const timeUntilExpiry = tokenExpiresAt - now;
    const refreshBeforeExpiry = 5 * 60 * 1000; // 5 minutes before expiry
    
    // If token expires in less than 5 minutes, refresh immediately
    if (timeUntilExpiry <= refreshBeforeExpiry) {
      refreshTokenProactively();
      return;
    }

    // Schedule refresh 5 minutes before expiry
    const timeUntilRefresh = timeUntilExpiry - refreshBeforeExpiry;
    
    const timeoutId = setTimeout(() => {
      refreshTokenProactively();
    }, timeUntilRefresh);

    return () => clearTimeout(timeoutId);
  }, [token, tokenExpiresAt]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        isLoading,
        apiBaseUrl: config.apiBaseUrl,
        requestPin,
        login,
        logout,
        switchAccount,
        loadAccounts,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
